import { useEffect, useId, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { MdAccountCircle, MdDelete, MdRefresh } from "react-icons/md";

export type AvatarUploadFieldProps = {
  isArabic: boolean;
  previewUrl?: string;
  storageKey?: string;
  uploading?: boolean;
  disabled?: boolean;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  emptyTitle?: string;
  emptyHint?: string;
  chooseLabel?: string;
  replaceLabel?: string;
  removeLabel?: string;
  accept?: string;
  className?: string;
};

function basenameFromKey(key: string): string {
  const parts = key.split("/").filter(Boolean);
  return parts[parts.length - 1] || key;
}

export default function AvatarUploadField({
  isArabic,
  previewUrl = "",
  storageKey = "",
  uploading = false,
  disabled = false,
  onFileChange,
  onRemove,
  emptyTitle,
  emptyHint,
  chooseLabel,
  replaceLabel,
  removeLabel,
  accept = "image/*",
  className = "",
}: AvatarUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [imageBroken, setImageBroken] = useState(false);

  const hasPreview = Boolean(previewUrl.trim()) && !imageBroken;

  useEffect(() => {
    setImageBroken(false);
  }, [previewUrl]);

  const copy = {
    emptyTitle: emptyTitle ?? (isArabic ? "لم يتم اختيار صورة بعد" : "No image selected yet"),
    emptyHint:
      emptyHint ??
      (isArabic ? "يمكنك رفع صورة المدرس من هنا" : "Upload a teacher image from here"),
    choose:
      chooseLabel ??
      (uploading
        ? isArabic
          ? "جارٍ الرفع..."
          : "Uploading..."
        : isArabic
        ? "اختيار صورة"
        : "Choose image"),
    replace: replaceLabel ?? (isArabic ? "استبدال الصورة" : "Replace image"),
    remove: removeLabel ?? (isArabic ? "إزالة الصورة" : "Remove image"),
  };

  const isDisabled = disabled || uploading;
  const displayName = storageKey ? basenameFromKey(storageKey) : "";

  const openFilePicker = () => {
    if (isDisabled) return;
    inputRef.current?.click();
  };

  const handlePlaceholderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isDisabled || hasPreview) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openFilePicker();
    }
  };

  return (
    <div className={className}>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        className="sr-only"
        disabled={isDisabled}
        onChange={onFileChange}
      />

      {!hasPreview ? (
        <div
          role="button"
          tabIndex={isDisabled ? -1 : 0}
          aria-disabled={isDisabled}
          aria-label={copy.emptyTitle}
          onClick={openFilePicker}
          onKeyDown={handlePlaceholderKeyDown}
          className={`flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-4 py-8 text-center transition-colors ${
            isDisabled
              ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-60 dark:border-white/10 dark:bg-white/5"
              : "cursor-pointer border-gray-300 bg-gray-50 hover:border-brand-400 hover:bg-brand-50/40 dark:border-white/10 dark:bg-white/5 dark:hover:border-brand-500/60 dark:hover:bg-brand-500/5"
          }`}
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-gray-200/90 dark:bg-navy-900 dark:ring-white/10">
            <MdAccountCircle className="h-10 w-10 text-gray-400 dark:text-gray-500" aria-hidden />
          </div>
          <div className="max-w-sm space-y-1">
            <p className="text-sm font-semibold text-gray-800 dark:text-white">{copy.emptyTitle}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{copy.emptyHint}</p>
          </div>
          <label
            htmlFor={inputId}
            onClick={(event) => event.stopPropagation()}
            className={`inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-xs font-semibold text-white transition hover:bg-brand-600 ${
              isDisabled ? "pointer-events-none opacity-70" : "cursor-pointer"
            }`}
          >
            {copy.choose}
          </label>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-gray-50/80 p-4 dark:border-white/10 dark:bg-white/5">
          <div
            className={`flex flex-col gap-4 sm:flex-row sm:items-center ${
              isArabic ? "sm:flex-row-reverse" : ""
            }`}
          >
            <div className="flex shrink-0 justify-center sm:justify-start">
              <div className="h-24 w-24 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/90 dark:bg-navy-900 dark:ring-white/10">
                <img
                  src={previewUrl}
                  alt={isArabic ? "معاينة الصورة" : "Image preview"}
                  className="h-full w-full object-cover"
                  onError={() => setImageBroken(true)}
                />
              </div>
            </div>

            <div className={`min-w-0 flex-1 space-y-3 ${isArabic ? "text-right" : "text-left"}`}>
              <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-white">
                  {isArabic ? "تم اختيار صورة" : "Image selected"}
                </p>
                {displayName ? (
                  <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400" title={displayName}>
                    {displayName}
                  </p>
                ) : null}
              </div>

              <div
                className={`flex flex-wrap items-center gap-2 ${
                  isArabic ? "justify-end sm:justify-start" : ""
                }`}
              >
                <label
                  htmlFor={inputId}
                  className={`inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-brand-600 ${
                    isDisabled ? "pointer-events-none opacity-70" : "cursor-pointer"
                  }`}
                >
                  <MdRefresh className="h-4 w-4" aria-hidden />
                  {uploading ? copy.choose : copy.replace}
                </label>
                <button
                  type="button"
                  onClick={onRemove}
                  disabled={isDisabled}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-600 transition hover:bg-gray-200 disabled:opacity-60 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
                >
                  <MdDelete className="h-4 w-4" aria-hidden />
                  {copy.remove}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
